import { useMemo, useState } from 'react';
import { ResponsiveLine, type LineSeries } from '@nivo/line';

import { lineTheme } from '@/components/Graphs/Charts/Line/LineHelpers';
import type { HistoricalScoreYearPoint } from '@/services/evidenceTimelineService';

type HistoricalScoreLineChartProps = {
  points: HistoricalScoreYearPoint[];
  loading?: boolean;
  rangeLabel?: string;
  secondaryMetric?: HistoricalScoreSecondaryMetric;
  onSecondaryMetricChange?: (metric: HistoricalScoreSecondaryMetric) => void;
  allowClinicalTrials?: boolean;
};

export type HistoricalScoreSecondaryMetric = 'publications' | 'clinicalTrials';

type ChartDatum = {
  x: string;
  y: number;
  rawScore?: number;
  rawCumulativeScore?: number;
  rawSecondary?: number;
};

const rootClass = 'flex h-full w-full flex-col';
const rootStyle = { minHeight: 'min(36vh, 300px)', minWidth: 240 } as const;
const plotClass = 'flex min-h-0 flex-1 flex-col w-full rounded-lg border border-slate-100 bg-white';
const labelStyle = { fontSize: '11px' } as const;

const ANNUAL_SCORE_SERIES_ID = 'Score';
const CUMULATIVE_SCORE_SERIES_ID = 'Cumulative score';

const SCORE_COLOR = '#be0080';
const CUMULATIVE_SCORE_COLOR = '#0d9488';
const SECONDARY_COLOR = '#2563eb';

function seriesColor(seriesId: string | number): string {
  if (seriesId === ANNUAL_SCORE_SERIES_ID) return SCORE_COLOR;
  if (seriesId === CUMULATIVE_SCORE_SERIES_ID) return CUMULATIVE_SCORE_COLOR;
  return SECONDARY_COLOR;
}

const SECONDARY_METRIC_CONFIG: Record<
  HistoricalScoreSecondaryMetric,
  { seriesId: string; axisLabel: string; legendLabel: string; tooltipLabel: string }
> = {
  publications: {
    seriesId: 'Publications',
    axisLabel: 'Publications',
    legendLabel: 'Annual publications',
    tooltipLabel: 'Publications',
  },
  clinicalTrials: {
    seriesId: 'Clinical trials',
    axisLabel: 'Clinical trials',
    legendLabel: 'Annual clinical trials',
    tooltipLabel: 'Clinical trials',
  },
};

function buildYScale(values: number[]) {
  if (values.length === 0) return { min: 0, max: 1 };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = Math.max((max - min) * 0.1, 0.02);
  return {
    min: Math.max(0, min - padding),
    max: max + padding,
  };
}

function mapSecondaryToScoreScale(
  value: number,
  maxSecondary: number,
  scoreScale: { min: number; max: number },
): number {
  if (maxSecondary <= 0) return scoreScale.min;
  const span = scoreScale.max - scoreScale.min;
  return scoreScale.min + (value / maxSecondary) * span;
}

function getSecondaryValue(
  point: HistoricalScoreYearPoint,
  metric: HistoricalScoreSecondaryMetric,
): number | null {
  const value = metric === 'publications' ? point.publications : point.clinicalTrials;
  return value != null && Number.isFinite(value) ? value : null;
}

function getFiniteNumber(value: number | null | undefined): number | null {
  return value != null && Number.isFinite(value) ? value : null;
}

export function HistoricalScoreLineChart({
  points,
  loading = false,
  rangeLabel,
  secondaryMetric: secondaryMetricProp,
  onSecondaryMetricChange,
  allowClinicalTrials = true,
}: HistoricalScoreLineChartProps) {
  const [internalSecondaryMetric, setInternalSecondaryMetric] =
    useState<HistoricalScoreSecondaryMetric>('publications');
  const secondaryMetric = secondaryMetricProp ?? internalSecondaryMetric;
  const effectiveSecondaryMetric =
    !allowClinicalTrials && secondaryMetric === 'clinicalTrials' ? 'publications' : secondaryMetric;

  const setSecondaryMetric = (metric: HistoricalScoreSecondaryMetric) => {
    if (!allowClinicalTrials && metric === 'clinicalTrials') return;
    if (secondaryMetricProp == null) {
      setInternalSecondaryMetric(metric);
    }
    onSecondaryMetricChange?.(metric);
  };
  const secondaryConfig = SECONDARY_METRIC_CONFIG[effectiveSecondaryMetric];
  const availableSecondaryMetrics = useMemo(
    () =>
      (Object.keys(SECONDARY_METRIC_CONFIG) as HistoricalScoreSecondaryMetric[]).filter(
        metric => allowClinicalTrials || metric !== 'clinicalTrials',
      ),
    [allowClinicalTrials],
  );
  const showMetricToggle = availableSecondaryMetrics.length > 1;

  const annualScoreValues = useMemo(
    () => points.map(p => getFiniteNumber(p.score)).filter((value): value is number => value != null),
    [points],
  );

  const cumulativeScoreValues = useMemo(
    () =>
      points.map(p => getFiniteNumber(p.cumulativeScore)).filter((value): value is number => value != null),
    [points],
  );

  const scoreAxisValues = useMemo(
    () => [...annualScoreValues, ...cumulativeScoreValues],
    [annualScoreValues, cumulativeScoreValues],
  );

  const secondaryValues = useMemo(
    () =>
      points
        .map(p => getSecondaryValue(p, effectiveSecondaryMetric))
        .filter((value): value is number => value != null),
    [points, effectiveSecondaryMetric],
  );

  const hasScoreAxis = scoreAxisValues.length > 0;
  const scoreScale = useMemo(() => buildYScale(scoreAxisValues), [scoreAxisValues]);
  const secondaryScale = useMemo(() => buildYScale(secondaryValues), [secondaryValues]);
  const plotScale = hasScoreAxis ? scoreScale : secondaryScale;
  const maxSecondary = useMemo(
    () => (secondaryValues.length > 0 ? Math.max(...secondaryValues) : 0),
    [secondaryValues],
  );

  const chartData = useMemo<LineSeries[]>(() => {
    const series: LineSeries[] = [];

    if (annualScoreValues.length > 0) {
      series.push({
        id: ANNUAL_SCORE_SERIES_ID,
        data: points
          .map((p): ChartDatum | null => {
            const score = getFiniteNumber(p.score);
            if (score == null) return null;
            return { x: String(p.year), y: score, rawScore: score };
          })
          .filter((datum): datum is ChartDatum => datum != null),
      });
    }

    if (cumulativeScoreValues.length > 0) {
      series.push({
        id: CUMULATIVE_SCORE_SERIES_ID,
        data: points
          .map((p): ChartDatum | null => {
            const cumulativeScore = getFiniteNumber(p.cumulativeScore);
            if (cumulativeScore == null) return null;
            return {
              x: String(p.year),
              y: cumulativeScore,
              rawCumulativeScore: cumulativeScore,
            };
          })
          .filter((datum): datum is ChartDatum => datum != null),
      });
    }

    if (secondaryValues.length > 0) {
      series.push({
        id: secondaryConfig.seriesId,
        data: points
          .map((p): ChartDatum | null => {
            const secondary = getSecondaryValue(p, effectiveSecondaryMetric);
            if (secondary == null) return null;
            const y = hasScoreAxis
              ? mapSecondaryToScoreScale(secondary, maxSecondary, scoreScale)
              : secondary;
            return {
              x: String(p.year),
              y,
              rawSecondary: secondary,
            };
          })
          .filter((datum): datum is ChartDatum => datum != null),
      });
    }

    return series;
  }, [
    annualScoreValues.length,
    cumulativeScoreValues.length,
    hasScoreAxis,
    maxSecondary,
    points,
    scoreScale,
    secondaryConfig.seriesId,
    effectiveSecondaryMetric,
    secondaryValues.length,
  ]);

  const hasChartData = chartData.some(series => series.data.length > 0);
  const showSecondaryAxis = secondaryValues.length > 0 && hasScoreAxis;
  const scoreSpan = scoreScale.max - scoreScale.min;

  const metricToggle = showMetricToggle ? (
    <div className="mb-2 flex shrink-0 flex-wrap gap-1">
      {availableSecondaryMetrics.map(metric => {
        const isActive = effectiveSecondaryMetric === metric;
        return (
          <button
            key={metric}
            type="button"
            onClick={() => setSecondaryMetric(metric)}
            className={`rounded-full border px-2.5 py-1 text-xs font-medium transition ${
              isActive
                ? 'border-blue bg-blue text-white'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            {SECONDARY_METRIC_CONFIG[metric].axisLabel}
          </button>
        );
      })}
    </div>
  ) : null;

  const header = (
    <div className="mb-2 flex shrink-0 items-baseline justify-between gap-2">
      <p className="font-semibold uppercase tracking-wide text-slate-500" style={labelStyle}>
        Scores & {secondaryConfig.axisLabel.toLowerCase()} by year
      </p>
      {rangeLabel ? (
        <p className="text-slate-400" style={labelStyle}>
          {rangeLabel}
        </p>
      ) : null}
    </div>
  );

  const legend = hasChartData ? (
    <div className="mb-2 flex shrink-0 flex-wrap gap-3">
      {annualScoreValues.length > 0 ? (
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: SCORE_COLOR }} />
          <span className="text-xs text-slate-600">Annual score</span>
        </div>
      ) : null}
      {cumulativeScoreValues.length > 0 ? (
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: CUMULATIVE_SCORE_COLOR }}
          />
          <span className="text-xs text-slate-600">Cumulative score</span>
        </div>
      ) : null}
      {secondaryValues.length > 0 ? (
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: SECONDARY_COLOR }} />
          <span className="text-xs text-slate-600">{secondaryConfig.legendLabel}</span>
        </div>
      ) : null}
    </div>
  ) : null;

  if (loading) {
    return (
      <div className={rootClass} style={rootStyle}>
        {header}
        {metricToggle}
        <div
          className={`${plotClass} items-center justify-center border-dashed border-slate-200 bg-slate-50/60`}
        >
          <p className="text-sm text-gray-500">Loading chart…</p>
        </div>
      </div>
    );
  }

  if (!hasChartData) {
    return (
      <div className={rootClass} style={rootStyle}>
        {header}
        {metricToggle}
        <div
          className={`${plotClass} items-center justify-center border-dashed border-slate-200 bg-slate-50/60 px-3 text-center`}
        >
          <div className="flex flex-col items-center gap-1">
            <p className="text-sm font-medium text-gray-500">No score data for this range.</p>
            {rangeLabel ? <p className="text-xs text-gray-400">Range: {rangeLabel}</p> : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass} style={rootStyle}>
      {header}
      {metricToggle}
      {legend}
      <div className={plotClass}>
        <ResponsiveLine
          data={chartData}
          margin={{
            top: 12,
            right: showSecondaryAxis ? 56 : 24,
            bottom: 56,
            left: 56,
          }}
          theme={lineTheme}
          colors={series => seriesColor(series.id)}
          lineWidth={2}
          curve="monotoneX"
          enablePoints
          pointSize={8}
          pointBorderWidth={2}
          pointBorderColor="#ffffff"
          useMesh
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: plotScale.min,
            max: plotScale.max,
            stacked: false,
          }}
          axisBottom={{
            tickSize: 4,
            tickPadding: 8,
            tickRotation: (chartData[0]?.data.length ?? 0) > 8 ? -65 : 0,
            legend: 'Year',
            legendOffset: 44,
            legendPosition: 'middle',
          }}
          axisLeft={{
            tickSize: 4,
            tickPadding: 8,
            legend: hasScoreAxis ? 'Score' : secondaryConfig.axisLabel,
            legendOffset: -48,
            legendPosition: 'middle',
            format: (value: number) => (hasScoreAxis ? value.toFixed(2) : String(Math.round(value))),
          }}
          axisRight={
            showSecondaryAxis
              ? {
                  tickSize: 4,
                  tickPadding: 8,
                  legend: secondaryConfig.axisLabel,
                  legendOffset: 48,
                  legendPosition: 'middle',
                  format: (value: number) => {
                    if (scoreSpan <= 0 || maxSecondary <= 0) return '0';
                    const scaled = ((value - scoreScale.min) / scoreSpan) * maxSecondary;
                    return String(Math.round(scaled));
                  },
                }
              : null
          }
          tooltip={({ point }) => {
            const year = String(point.data.x);
            const original = points.find(p => String(p.year) === year);
            const annualScore = original ? getFiniteNumber(original.score) : null;
            const cumulativeScore = original ? getFiniteNumber(original.cumulativeScore) : null;
            const secondary = original ? getSecondaryValue(original, effectiveSecondaryMetric) : null;

            return (
              <div className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-md">
                <div className="font-semibold text-slate-800">{year}</div>
                {annualScore != null ? (
                  <div className="text-slate-600">Annual score: {annualScore.toFixed(3)}</div>
                ) : null}
                {cumulativeScore != null ? (
                  <div className="text-slate-600">Cumulative score: {cumulativeScore.toFixed(3)}</div>
                ) : null}
                {secondary != null ? (
                  <div className="text-slate-600">
                    {secondaryConfig.tooltipLabel}: {secondary}
                  </div>
                ) : null}
              </div>
            );
          }}
        />
      </div>
    </div>
  );
}
